import React, { useState, useEffect, useMemo , useRef} from 'react';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';

const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// BOM Type options (unchanged)
const BOM_TYPE_OPTIONS = [
  { value: 'Production BOM', label: 'Production BOM' },
  { value: 'Assembly BOM', label: 'Assembly BOM' },
  { value: 'Packing BOM', label: 'Packing BOM' },
];

// Status options (unchanged)
const STATUS_OPTIONS = [
  { value: 'New', label: 'New' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const initialHeader = {
  bomType: 'Production BOM',
  orderNumber: '',
  productCode: '',
  productName: '',
  uom: '',
  uomId: '',
  qty: '',
  status: 'New',
  bomHeaderId: '',
  department_id: "", // Add department_id field
};

const initialItem = {
  materialType: '',
  productCode: '',
  productName: '',
  uom: '',
  uomId: '',
  qty: '',
  flushType: '',
  calculatedQty: '',
  department_id: "", // Add department_id field
};

const ProductionOrderFormModal = ({ open, onClose, onSubmit, initialData, isEdit, formData }) => {
  const [header, setHeader] = useState(initialHeader);
  const [items, setItems] = useState([{ ...initialItem }]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    products: [],
    uoms: [],
    nextOrderNumber: '',
  });
  
  // Added state for user management
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // New state for maximize/minimize functionality
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Department-related state (matching StockIn implementation)
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  // Function to fetch current user (unchanged)
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch departments (matching StockIn implementation)
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`);
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration (matching StockIn implementation)
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the ProductionOrder screen configuration
      const productionOrderScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'ProductionOrder'.toLowerCase()
      );
      
      console.log('ProductionOrder screen configuration:', productionOrderScreen);
      if (productionOrderScreen) {
        setScreenConfig(productionOrderScreen);
        // Show department field if ShowOnHeader is true
        const shouldShowDepartment = productionOrderScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // Show department item field if ShowOnItem is true
        const shouldShowDepartmentItem = productionOrderScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
        
        // If department fields should be shown, fetch departments
        if (shouldShowDepartment || shouldShowDepartmentItem) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };
  
  // Toggle maximize/minimize function (unchanged)
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  useEffect(() => {
    if (open) {
      // Reset maximize state when modal opens
      setIsMaximized(false);
      
      // Fetch current user when modal opens
      fetchCurrentUser();
      fetchScreenConfig(); // Fetch screen config
      
      const initializeForm = async () => {
        try {
          // Try to get user from token (unchanged)
          let currentUser = null;
          try {
            const token = localStorage.getItem('authToken');
            if (!token) {
              throw new Error('No authentication token found');
            }
            
            // Decode the JWT token
            const parts = token.split('.');
            if (parts.length !== 3) {
              throw new Error('Invalid token format');
            }
            
            // Decode the payload
            const payload = JSON.parse(atob(parts[1]));
            
            // Extract user information from the payload
            currentUser = {
              UserID: payload.userId,
              UserName: payload.email
            };
          } catch (error) {
            console.error('Error extracting user from token:', error);
          }
          
          if (!currentUser || !currentUser.UserID) {
            console.error('Could not extract user information from token');
            alert('You must be logged in to create Production Order records');
            onClose();
            return;
          }
          
          // Fetch additional user details from the API (unchanged)
          try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_URL}/users/${currentUser.UserID}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const userDetails = await response.json();
              console.log('Fetched user details:', userDetails);
              
              // Update with the actual username from the database
              currentUser.UserName = userDetails.Username || userDetails.FullName || userDetails.Email;
            }
          } catch (error) {
            console.error('Error fetching user details:', error);
          }
          
          // If we have user info, proceed with form initialization
          console.log('User authenticated:', currentUser);
          
          // Use formData from props if available, otherwise fetch it
          if (formData && formData.products && formData.products.length > 0) {
            setDropdownData({
              products: formData.products || [],
              uoms: formData.uoms || [],
              nextOrderNumber: formData.nextOrderNumber || '',
            });
          } else {
            await fetchDropdownData();
          }
          
          if (isEdit && initialData) {
            // Debug logging
            console.log('Initial data received:', initialData);
            
            // Set header data for edit mode
            setHeader({
              bomType: initialData.BOMType || 'Production BOM',
              orderNumber: initialData.ProductionOrderID || '',
              productCode: initialData.ProductCode || '',
              productName: initialData.ProductName || '',
              uom: initialData.UOMName || initialData.UOM || '',
              uomId: initialData.UOMID || '',
              qty: initialData.Qty?.toString() || '',
              status: initialData.Status || 'New',
              bomHeaderId: initialData.BOMHeaderId || '',
              department_id: initialData.DepartmentID?.toString() || "", // Add department
            });
            
            // Set items data
            if (initialData.Details && initialData.Details.length > 0) {
              setItems(initialData.Details.map(item => ({
                materialType: item.MaterialType || '',
                productCode: item.ProductCode || '',
                productName: item.ProductName || '',
                uom: item.UOMName || item.UOM || '',
                uomId: item.UOMID || '',
                qty: item.Qty?.toString() || '',
                flushType: item.FlushType || '',
                calculatedQty: item.CalculatedQty?.toString() || '',
                department_id: item.DepartmentID?.toString() || "", // Add department
              })));
            } else {
              setItems([{ ...initialItem }]);
            }
          } else {
            // For new records
            setHeader({ 
              ...initialHeader, 
              orderNumber: formData?.nextOrderNumber || '',
              department_id: "" // Initialize empty department
            });
            setItems([{ ...initialItem }]);
          }
          
          setErrors({});
          setIsSubmitting(false);
        } catch (error) {
          console.error('Error initializing form:', error);
          alert('Error initializing form. Please try again.');
          onClose();
        }
      };
      
      initializeForm();
    }
  }, [open, initialData, isEdit, onClose, formData]);
  
  // Add this useEffect to monitor the header state
  useEffect(() => {
    console.log('Header state updated:', header);
  }, [header]);
  
  const fetchDropdownData = async () => {
    try {
      setLoading(true);
      
      // Fetch products
      const productsResponse = await fetch(`${API_URL}/production-order/form-data`);
      if (!productsResponse.ok) throw new Error('Failed to fetch form data');
      const productsData = await productsResponse.json();
      
      // Fetch UOMs
      const uomsResponse = await fetch(`${API_URL}/uoms`);
      if (!uomsResponse.ok) throw new Error('Failed to fetch UOMs');
      const uomsData = await uomsResponse.json();
      
      setDropdownData({
        products: productsData.products || [],
        uoms: uomsData || [],
        nextOrderNumber: productsData.nextOrderNumber || '',
      });
      
      // Set order number if creating new
      if (!isEdit) {
        setHeader(prev => ({ 
          ...prev, 
          orderNumber: productsData.nextOrderNumber || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      alert('Error loading form data');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch BOM details when product and BOM type are selected
  const fetchBOMDetails = async (productCode, bomType) => {
    if (!productCode || !bomType) return;
    // Normalize BOM type for backend safety
  const bomTypeClean = encodeURIComponent(bomType.trim());
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/production-order/product/${productCode}?bomType=${bomTypeClean}`);
      console.log("printing fetchBOMDetails response: " + response);
      if (!response.ok) throw new Error('Failed to fetch BOM details');
      
      const data = await response.json();
      console.log('BOM details fetched:', data);
      
      if (data.header && data.details) {
        // Get UOM ID and name from the BOM header
        let headerUomId = '';
        let headerUomName = '';
        
        // Check if UOMID is provided in the response
        if (data.header.UOMID) {
          headerUomId = data.header.UOMID.toString();
          // Find UOM name by ID
          const uomObj = dropdownData.uoms.find(u => u.UOMID === headerUomId);
          headerUomName = uomObj ? uomObj.UOMName : '';
        } 
        // If UOMID not available but UOM is provided, try to find by name
        else if (data.header.UOM) {
          headerUomName = data.header.UOM;
          // Find UOM ID by name
          const uomObj = dropdownData.uoms.find(u => u.UOMName === headerUomName);
          headerUomId = uomObj ? uomObj.UOMID.toString() : '';
        }
        
        // Update header with product details and BOM header ID
        setHeader(prev => ({
          ...prev,
          productCode: data.header.ProductCode,
          productName: data.header.ProductName,
          uom: headerUomName,
          uomId: headerUomId,
          bomHeaderId: data.header.ProductionBOMID || '',
        }));
        
        // Update items with BOM details
        const newItems = data.details.map(detail => {
          // Get UOM ID and name for each detail
          let detailUomId = '';
          let detailUomName = '';
          
          // Check if UOMID is provided in the response
          if (detail.UOMID) {
            detailUomId = detail.UOMID.toString();
            // Find UOM name by ID
            const uomObj = dropdownData.uoms.find(u => u.UOMID === detailUomId);
            detailUomName = uomObj ? uomObj.UOMName : '';
          } 
          // If UOMID not available but UOM is provided, try to find by name
          else if (detail.UOM) {
            detailUomName = detail.UOM;
            // Find UOM ID by name
            const uomObj = dropdownData.uoms.find(u => u.UOMName === detailUomName);
            detailUomId = uomObj ? uomObj.UOMID.toString() : '';
          }
          
          return {
            materialType: detail.MaterialType,
            productCode: detail.ProductCode,
            productName: detail.ProductName,
            uom: detailUomName,
            uomId: detailUomId,
            qty: detail.Qty?.toString() || '0',
            flushType: detail.FlushType,
            calculatedQty: calculateCalculatedQty(detail.Qty?.toString() || '0', header.qty),
            department_id: detail.DepartmentID?.toString() || "", // Add department
          };
        });
        
        setItems(newItems.length > 0 ? newItems : [{ ...initialItem }]);
      }
    } catch (error) {
      console.error('Error fetching BOM details:', error);
      alert('Error loading BOM details. Please check if a BOM exists for this product and type.');
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate the quantity for production order (header qty * detail qty)
  const calculateCalculatedQty = (detailQty, headerQty) => {
    const detail = parseFloat(detailQty) || 0;
    const header = parseFloat(headerQty) || 0;
    return (detail * header).toFixed(2);
  };
  
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader({ ...header, [name]: value });
    
    // If BOM type or product code changes, fetch BOM details
    if (name === 'bomType' || name === 'productCode') {
      const productCode = name === 'productCode' ? value : header.productCode;
      const bomType = name === 'bomType' ? value : header.bomType;
      
      // Clear BOM header ID when product or BOM type changes
      setHeader(prev => ({
        ...prev,
        bomHeaderId: '',
        uom: '',
        uomId: ''
      }));
      
      if (productCode && bomType) {
        fetchBOMDetails(productCode, bomType);
      }
    }
    
    // If quantity changes, update calculated quantities
    if (name === 'qty') {
      const newItems = items.map(item => ({
        ...item,
        calculatedQty: calculateCalculatedQty(item.qty, value)
      }));
      setItems(newItems);
    }
  };
  
  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    const newItems = [...items];
    newItems[idx][name] = value;
    
    // If quantity changes, update calculated quantity
    if (name === 'qty') {
      newItems[idx].calculatedQty = calculateCalculatedQty(value, header.qty);
    }
    
    setItems(newItems);
  };
  
  // Handle product selection - auto-fill product details
  const handleProductChange = (idx, e) => {
    const selectedProductCode = e.target.value;
    const newItems = [...items];
    
    if (selectedProductCode) {
      const selectedProduct = dropdownData.products.find(p => p.ProductCode === selectedProductCode);
      if (selectedProduct) {
        // Get UOM ID and name from the selected product
        let uomId = '';
        let uomName = '';
        
        // If UOMID is available, use it to find the UOM name
        if (selectedProduct.UOMID) {
          uomId = selectedProduct.UOMID.toString();
          const uomObj = dropdownData.uoms.find(u => u.UOMID === uomId);
          uomName = uomObj ? uomObj.UOMName : '';
        } 
        // If UOMName is available, use it to find the UOM ID
        else if (selectedProduct.UOMName) {
          uomName = selectedProduct.UOMName;
          const uomObj = dropdownData.uoms.find(u => u.UOMName === uomName);
          uomId = uomObj ? uomObj.UOMID.toString() : '';
        }
        
        // Auto-fill all product-related fields
        newItems[idx] = {
          ...newItems[idx],
          productCode: selectedProduct.ProductCode,
          productName: selectedProduct.ProductName,
          uom: uomName,
          uomId: uomId,
        };
      }
    } else {
      // Clear product-related fields if no product is selected
      newItems[idx] = {
        ...newItems[idx],
        productCode: '',
        productName: '',
        uom: '',
        uomId: '',
      };
    }
    
    setItems(newItems);
  };
  
  const addItem = () => {
    setItems([...items, { 
      ...initialItem,
      department_id: showDepartmentItemField ? "" : undefined, // Only include if department field is shown
    }]);
  };
  
  const removeItem = (idx) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== idx));
    }
  };
  
  const filteredProducts = useMemo(() => {
    return dropdownData.products.filter(product => product.ProductType !== 'Finished');
  }, [dropdownData.products]);
  
  const validate = () => {
    const errs = {};
    if (!header.bomType) errs.bomType = 'Required';
    if (!header.orderNumber || !header.orderNumber.trim()) errs.orderNumber = 'Required';
    if (!header.productCode) errs.productCode = 'Required';
    if (!header.productName) errs.productName = 'Required';
    if (!header.uom) errs.uom = 'Required'; // Changed from uomId to uom
    if (!header.qty) errs.qty = 'Required';
    if (!header.status) errs.status = 'Required';
    
    // Validate department field only if it's shown (matching StockIn implementation)
    if (showDepartmentField && !header.department_id) errs.department_id = 'Required';
    
    items.forEach((item, idx) => {
      if (!item.materialType) errs[`materialType_${idx}`] = 'Required';
      if (!item.productCode) errs[`productCode_${idx}`] = 'Required';
      if (!item.productName) errs[`productName_${idx}`] = 'Required';
      if (!item.uom) errs[`uom_${idx}`] = 'Required'; // Changed from uomId to uom
      if (!item.qty) errs[`qty_${idx}`] = 'Required';
      if (!item.flushType) errs[`flushType_${idx}`] = 'Required';
      
      // Validate department item field only if it's shown (matching StockIn implementation)
      if (showDepartmentItemField && !item.department_id) errs[`department_id_${idx}`] = 'Required';
      
      // Check if the product is finished
      const product = dropdownData.products.find(p => p.ProductCode === item.productCode);
      if (product && product.ProductType === 'Finished') {
        errs[`productCode_${idx}`] = 'Finished products are not allowed';
      }
    });
    
    console.log("Validation errors:", errs);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  const handleSubmit = async (e) => {
    console.log("handleSubmit called");
    
    // Prevent default form submission
    if (e) {
      e.preventDefault();
    }
    
    const isValid = validate();
    console.log("Form validation result:", isValid);
    if (!isValid || isSubmitting) {
      console.log("Form validation failed or already submitting");
      return;
    }
    
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      console.log("User not valid:", { isUserValid, currentUser });
      alert(authError || 'User information not available. Please refresh the page and log in again.');
      return;
    }
    
    console.log("Starting submission process");
    setIsSubmitting(true);
    setLoading(true);
    
    try {
      // Convert CreatedBy to string to match database expectations
      const createdBy = String(currentUser.UserID);
      
      const payload = {
  ProductionOrderID: header.orderNumber,
  BOMType: header.bomType,
  ProductCode: header.productCode,
  ProductName: header.productName,
  Qty: parseFloat(header.qty) || 0,
  UOM: header.uom ? String(header.uom) : null, // Convert to string
  Status: header.status,
  CreatedBy: createdBy,
  UpdatedBy: createdBy,
  BOMHeaderId: header.bomHeaderId,
  // Only include department_id if showDepartmentField is true (matching StockIn implementation)
  ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
  Details: items.map(item => ({
    MaterialType: item.materialType,
    ProductCode: item.productCode,
    ProductName: item.productName,
    Qty: parseFloat(item.qty) || 0,
    CalculatedQty: parseFloat(item.calculatedQty) || 0,
    FlushType: item.flushType,
    UOM: item.uom ? String(item.uom) : null, // Convert to string
    CreatedBy: createdBy,
    UpdatedBy: createdBy,
    // Only include department_id if showDepartmentItemField is true (matching StockIn implementation)
    ...(showDepartmentItemField && item.department_id ? { department_id: parseInt(item.department_id) } : {})
  }))
};
      
      console.log("Sending payload:", JSON.stringify(payload, null, 2));
      
      const url = isEdit 
        ? `${API_URL}/production-order/${header.orderNumber}`
        : `${API_URL}/production-order`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      console.log("Sending request to:", url);
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });
      
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Backend error:", errorData);
        throw new Error(errorData.message || 'Failed to save Production Order record');
      }
      
      const result = await response.json();
      console.log("Backend response:", result);
      
      // Call onSubmit to refresh data in parent component
      if (onSubmit) {
        onSubmit(result); // Pass the result to parent component
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving Production Order record:', err);
      alert(err.message || 'Error saving Production Order record');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };
  
  if (!open) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      } ${
        isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'
      }`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Production Order' : 'Create Production Order'}</h2>
            <div className="flex items-center gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* User validation warning */}
        {open && !isUserValid && (
          <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  {authError || 'User information not available. Please refresh the page and log in again.'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Scrollable Content Area */}
        <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
          <div className="space-y-6 pb-6">
            {/* Production Order Header */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">Production Order Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">BOM Type *</label>
                    <SearchableDropdown
    options={BOM_TYPE_OPTIONS}
    value={header.bomType}
    onChange={(value) => handleHeaderChange({ target: { name: 'bomType', value } })}
    placeholder="Select BOM Type"
    error={errors.bomType}
  />
                  {errors.bomType && <span className="text-xs text-red-500">{errors.bomType}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Order Number *</label>
                  <input 
                    name="orderNumber" 
                    value={header.orderNumber} 
                    onChange={handleHeaderChange} 
                    disabled
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                  />
                  {errors.orderNumber && <span className="text-xs text-red-500">{errors.orderNumber}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Output *</label>
                 <SearchableDropdown
    options={filteredProducts}
    value={header.productCode}
    onChange={(value) => handleHeaderChange({ target: { name: 'productCode', value } })}
    getOptionLabel={(product) => `${product.ProductCode} - ${product.ProductName}`}
    getOptionValue={(product) => product.ProductCode}
    getOptionSecondaryLabel={(product) => product.ProductName}
    placeholder="Select Product"
    error={errors.productCode}
  />
                  {errors.productCode && <span className="text-xs text-red-500">{errors.productCode}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">BOM ID</label>
                  <input 
                    name="bomHeaderId" 
                    value={header.bomHeaderId} 
                    readOnly 
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Quantity *</label>
                  <input 
                    name="qty" 
                    type="number" 
                    min="0" 
                    step="0.01"
                    value={header.qty} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 ${errors.qty ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.qty && <span className="text-xs text-red-500">{errors.qty}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">UOM *</label>
                  <input 
                    name="uom" 
                    value={header.uom} 
                    readOnly 
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                  />
                  {errors.uom && <span className="text-xs text-red-500">{errors.uom}</span>}
                </div>
                
                {/* Department Selection Field - Only shown if ShowOnHeader is true (matching StockIn implementation) */}
                {showDepartmentField && (
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Department *
                    </label>
                   <SearchableDropdown
      options={departments}
      value={header.department_id}
      onChange={(value) => handleHeaderChange({ target: { name: 'department_id', value } })}
      getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
      getOptionValue={(dept) => dept.DepartmentID.toString()}
      getOptionSecondaryLabel={(dept) => dept.DepartmentName}
      placeholder={loadingDepartments ? 'Loading departments...' : 'Select Department'}
      error={errors.department_id}
      disabled={loadingDepartments}
      className="uppercase"
    />
                    {errors.department_id && (
                      <span className="text-xs text-red-500">
                        {errors.department_id}
                      </span>
                    )}
                  </div>
                )}
                
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Status *</label>
                  <select 
                    name="status" 
                    value={header.status} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 ${errors.status ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {errors.status && <span className="text-xs text-red-500">{errors.status}</span>}
                </div>
              </div>
            </div>
            
            {/* Production Order Materials */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#384959]">Materials Required</h3>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" 
                  onClick={addItem}
                >
                  <PlusIcon className="mr-1" /> Add Material
                </button>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full border border-[#E2E8F0]">
                  <thead>
                    <tr className="bg-[#BDDDFC] text-[#384959]">
                      <th className="p-2">#</th>
                      <th className="p-2">Material Type</th>
                      <th className="p-2">Product</th>
                      <th className="p-2">Product Code</th>
                      <th className="p-2">Product Name</th>
                      <th className="p-2">UOM</th>
                      <th className="p-2">BOM Qty</th>
                      <th className="p-2">Production Order Qty</th>
                      <th className="p-2">Flush Type</th>
                      {/* Department column - Only shown if ShowOnItem is true (matching StockIn implementation) */}
                      {showDepartmentItemField && <th className="p-2">Department</th>}
                      <th className="p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx} className="border-t border-[#E2E8F0]">
                        <td className="p-2 text-center">{idx + 1}</td>
                        <td className="p-2">
                          <input 
                            name="materialType" 
                            value={item.materialType} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-32 border rounded-lg px-2 py-1 ${errors[`materialType_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`materialType_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <select 
                            name="productCode" 
                            value={item.productCode} 
                            onChange={e => handleProductChange(idx, e)} 
                            className={`w-40 border rounded-lg px-2 py-1 ${errors[`productCode_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          >
                            <option value="">Select Product</option>
                            {filteredProducts.map(product => (
                              <option key={product.ProductCode} value={product.ProductCode}>
                                {product.ProductName}
                              </option>
                            ))}
                          </select>
                          {errors[`productCode_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="productCodeDisplay" 
                            value={item.productCode} 
                            readOnly 
                            className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                            title="Auto-populated from product selection"
                          />
                        </td>
                        <td className="p-2">
                          <input 
                            name="productName" 
                            value={item.productName} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-40 border rounded-lg px-2 py-1 ${errors[`productName_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`productName_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="uom" 
                            value={item.uom} 
                            readOnly 
                            className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                            title="Auto-populated from product selection"
                          />
                          {errors[`uom_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="qty" 
                            type="number" 
                            min="0" 
                            step="0.01"
                            value={item.qty} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-16 border rounded-lg px-2 py-1 ${errors[`qty_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`qty_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="calculatedQty" 
                            value={item.calculatedQty} 
                            readOnly 
                            className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                            title="Auto-calculated based on header quantity"
                          />
                        </td>
                        <td className="p-2">
                          <input 
                            name="flushType" 
                            value={item.flushType} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-24 border rounded-lg px-2 py-1 ${errors[`flushType_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`flushType_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        
                        {/* Department cell - Only shown if ShowOnItem is true (matching StockIn implementation) */}
                        {showDepartmentItemField && (
                          <td className="p-2">
                           <SearchableDropdown
      options={departments}
      value={item.department_id}
      onChange={(value) => handleItemChange(idx, { target: { name: 'department_id', value } })}
      getOptionLabel={(dept) => dept.DepartmentCode}
      getOptionValue={(dept) => dept.DepartmentID.toString()}
      getOptionSecondaryLabel={(dept) => dept.DepartmentName}
      placeholder="Select"
      width="w-32"
      error={errors[`department_id_${idx}`]}
      className="uppercase"
    />
                            {errors[`department_id_${idx}`] && (
                              <span className="text-xs text-red-500">*</span>
                            )}
                          </td>
                        )}
                        
                        <td className="p-2 text-center">
                          <button 
                            type="button" 
                            className="text-red-500 hover:text-red-700 transition-colors" 
                            onClick={() => removeItem(idx)} 
                            disabled={items.length === 1}
                          >
                            <TrashIcon />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="button"
              className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={loading || isSubmitting || !isUserValid}
              onClick={handleSubmit}
            >
              {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update Order' : 'Create Order')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionOrderFormModal;